import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type MutableRefObject,
} from "react";
import { createPortal } from "react-dom";

// =============================================================================
// CoursePicker — the course-doc dropdown on the course panels (phone + closer)
// =============================================================================
// Replaces the native <select>. Mako, 2026-10-01: "for the dropdown in the
// course picker make sure it uses /triangle and it also uses /animate".
//
// SHAPE: hover the trigger → a panel drops down (click pins it, tap on touch).
// Grouped courses (phone setter: Start here / Inbound / Outbound / Cross-checks)
// show a GROUP RAIL on the left and that group's docs on the right. Ungrouped
// courses (closer stages) show one list.
//
// TWO SAFE TRIANGLES (/triangle):
//   1. Trigger → panel, "defer the CLOSE" (PipelineControlMenu geometry): a
//      cursor diving from the trigger toward the panel below keeps it open.
//   2. Group rail → doc list, "defer the SIBLING SWITCH" (Sidebar
//      ExecCategoryNav geometry): hover "Inbound", then move diagonally to a
//      doc lower down. Grazing "Outbound" on the way must NOT swap the list.
//      Only a cursor that SETTLES on a group row switches it.
//
// MOTION (/animate): panel pops from the trigger (`anim-popover-in` on a
// NON-glass wrapper; the `detail-overlay` surface itself never transforms),
// chevron springs (`anim-chevron`), each group's doc list cascades in
// (`anim-cascade`, re-keyed per group), the trigger label pops on change.
//
// GLASS: Portal'd to body, `detail-overlay` surface (see /triangle gotchas).
// =============================================================================

const FLYOUT_CLOSE_DELAY = 220; // ms, grace before the panel closes on leave
const OPEN_DELAY = 120; // ms, hover intent, so a passing cursor doesn't flash it open
const AIM_RECHECK = 200; // ms, re-check cadence while the cursor aims somewhere
const MOUSE_TRACK = 3; // recent cursor samples kept for the aim math

type Pt = { x: number; y: number };
// dy/dx, for the rail → list triangle (list opens to the RIGHT).
const slope = (a: Pt, b: Pt) => (b.y - a.y) / (b.x - a.x || 0.0001);
// dx/dy, for the trigger → panel triangle (panel opens BELOW).
const islope = (a: Pt, b: Pt) => (b.x - a.x) / (b.y - a.y || 0.0001);

export interface CoursePickerItem {
  file: string;
  title: string;
  /** Optional group. Two or more distinct groups turn on the group rail. */
  group?: string;
}

interface CoursePickerProps {
  items: CoursePickerItem[];
  value: string;
  onChange: (file: string) => void;
  testId: string;
  ariaLabel?: string;
}

// "Inbound · Fresh Applications" under group "Inbound" → "Fresh Applications".
const shortTitle = (it: CoursePickerItem) =>
  it.group && it.title.startsWith(`${it.group} · `)
    ? it.title.slice(it.group.length + 3)
    : it.title;

export default function CoursePicker({
  items,
  value,
  onChange,
  testId,
  ariaLabel = "Pick a course doc",
}: CoursePickerProps) {
  const groups = useMemo(() => {
    const order: string[] = [];
    for (const it of items) if (it.group && !order.includes(it.group)) order.push(it.group);
    return order;
  }, [items]);
  const grouped = groups.length > 1;

  const current = items.find((i) => i.file === value);
  const currentGroup = current?.group ?? groups[0] ?? null;

  const [open, setOpen] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [shownGroup, setShownGroup] = useState<string | null>(currentGroup);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const hideTimer = useRef<number | null>(null);
  const openTimer = useRef<number | null>(null);
  const aimTimer = useRef<number | null>(null); // trigger → panel close deferral
  const railTimer = useRef<number | null>(null); // rail → list switch deferral
  const pinnedRef = useRef(false); // timer callbacks read this, not a stale closure
  const mouseLocs = useRef<Pt[]>([]);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0, opacity: 0 });

  const clear = (t: MutableRefObject<number | null>) => {
    if (t.current) {
      window.clearTimeout(t.current);
      t.current = null;
    }
  };
  const clearAll = () => {
    clear(hideTimer);
    clear(openTimer);
    clear(aimTimer);
    clear(railTimer);
  };

  // ── Triangle 1: is the cursor diving from the trigger into the panel below? ──
  const aimingAtPanel = () => {
    const panel = panelRef.current;
    if (!open || !panel) return false;
    const locs = mouseLocs.current;
    const loc = locs[locs.length - 1];
    const prev = locs[locs.length - 2] || loc;
    if (!loc || loc === prev) return false;
    const r = panel.getBoundingClientRect();
    const BUF = 40;
    const left = { x: r.left - BUF, y: r.top };
    const right = { x: r.right + BUF, y: r.top };
    return islope(loc, left) < islope(prev, left) && islope(loc, right) > islope(prev, right);
  };

  const maybeHide = () => {
    if (pinnedRef.current) return;
    if (aimingAtPanel()) {
      const settledAt = mouseLocs.current[mouseLocs.current.length - 1];
      aimTimer.current = window.setTimeout(() => {
        const now = mouseLocs.current[mouseLocs.current.length - 1];
        if (now === settledAt) {
          clear(aimTimer);
          setOpen(false);
        } else {
          maybeHide();
        }
      }, AIM_RECHECK);
    } else {
      setOpen(false);
    }
  };
  const scheduleHide = () => {
    clear(hideTimer);
    clear(openTimer);
    clear(aimTimer);
    hideTimer.current = window.setTimeout(maybeHide, FLYOUT_CLOSE_DELAY);
  };

  const openNow = () => {
    clearAll();
    if (!open) setShownGroup(currentGroup); // every open starts on the doc you're reading
    setOpen(true);
  };
  const openSoon = () => {
    clear(hideTimer);
    clear(aimTimer);
    if (open) return;
    clear(openTimer);
    openTimer.current = window.setTimeout(openNow, OPEN_DELAY);
  };
  const togglePin = () => {
    clearAll();
    const next = !pinned;
    setPinned(next);
    pinnedRef.current = next;
    if (next && !open) setShownGroup(currentGroup);
    setOpen(next);
  };
  const closeAll = () => {
    clearAll();
    setPinned(false);
    pinnedRef.current = false;
    setOpen(false);
  };
  const select = (file: string) => {
    onChange(file);
    closeAll();
  };

  // ── Triangle 2: is the cursor diving from the rail into the doc list? ──
  const aimingAtList = () => {
    const list = listRef.current;
    if (!list) return false;
    const locs = mouseLocs.current;
    const loc = locs[locs.length - 1];
    const prev = locs[locs.length - 2] || loc;
    if (!loc || loc === prev) return false;
    const r = list.getBoundingClientRect();
    const BUF = 40;
    const top = { x: r.left, y: r.top - BUF };
    const bot = { x: r.left, y: r.bottom + BUF };
    return slope(loc, top) < slope(prev, top) && slope(loc, bot) > slope(prev, bot);
  };
  const possiblySwitch = (g: string) => {
    clear(railTimer);
    if (g === shownGroup) return;
    if (aimingAtList()) {
      // Fresh object per mousemove → identity says "has the cursor moved since?"
      const settledAt = mouseLocs.current[mouseLocs.current.length - 1];
      railTimer.current = window.setTimeout(() => {
        const now = mouseLocs.current[mouseLocs.current.length - 1];
        if (now === settledAt) {
          clear(railTimer);
          setShownGroup(g);
        } else {
          possiblySwitch(g);
        }
      }, AIM_RECHECK);
    } else {
      setShownGroup(g);
    }
  };
  // Keyboard focus / tap: no dive on Tab or touch, switch at once.
  const switchNow = (g: string) => {
    clear(railTimer);
    setShownGroup(g);
  };

  useEffect(
    () => () => {
      for (const t of [hideTimer, openTimer, aimTimer, railTimer])
        if (t.current) window.clearTimeout(t.current);
    },
    []
  );

  // Global cursor track (panel is Portal'd, so a scoped listener misses the
  // transit), Escape to close, outside pointerdown to dismiss a pinned panel.
  useEffect(() => {
    if (!open) return;
    const onMove = (e: MouseEvent) => {
      const locs = mouseLocs.current;
      locs.push({ x: e.clientX, y: e.clientY });
      if (locs.length > MOUSE_TRACK) locs.shift();
    };
    const shut = () => {
      for (const t of [hideTimer, openTimer, aimTimer, railTimer]) {
        if (t.current) window.clearTimeout(t.current);
        t.current = null;
      }
      setPinned(false);
      pinnedRef.current = false;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") shut();
    };
    const onDown = (e: PointerEvent) => {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t) || panelRef.current?.contains(t)) return;
      shut();
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("keydown", onKey);
    window.addEventListener("pointerdown", onDown);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("pointerdown", onDown);
      mouseLocs.current = [];
    };
  }, [open]);

  // Position under the trigger, same width (fixed coords, Portal is on body).
  useLayoutEffect(() => {
    if (!open) return;
    const compute = () => {
      const trigger = triggerRef.current;
      const panel = panelRef.current;
      if (!trigger || !panel) return;
      const r = trigger.getBoundingClientRect();
      const width = Math.max(r.width, grouped ? 360 : 240);
      let left = r.left;
      const maxLeft = window.innerWidth - width - 12;
      if (left > maxLeft) left = Math.max(12, maxLeft);
      let top = r.bottom + 6;
      const maxTop = window.innerHeight - panel.offsetHeight - 12;
      if (top > maxTop) top = Math.max(12, maxTop);
      setPos({ top: Math.round(top), left: Math.round(left), width, opacity: 1 });
    };
    compute();
    window.addEventListener("resize", compute);
    window.addEventListener("scroll", compute, true);
    return () => {
      window.removeEventListener("resize", compute);
      window.removeEventListener("scroll", compute, true);
    };
  }, [open, grouped]);

  const listItems = grouped ? items.filter((i) => i.group === shownGroup) : items;

  const renderItem = (it: CoursePickerItem) => {
    const active = it.file === value;
    return (
      <button
        key={it.file}
        type="button"
        role="menuitemradio"
        aria-checked={active}
        data-testid={`${testId}-option`}
        data-value={it.file}
        onClick={() => select(it.file)}
        className={`anim-row cursor-pointer w-full flex items-center justify-between gap-3 text-left px-2.5 py-1.5 rounded-lg token-13 transition-colors ${
          active
            ? "bg-ds-accent/[0.08] text-ds-accent font-medium"
            : "text-glass-secondary hover:bg-black/[0.04] hover:text-glass"
        }`}
      >
        <span className="truncate">{grouped ? shortTitle(it) : it.title}</span>
        {active && (
          <span className="shrink-0 text-[10px] leading-none" aria-hidden="true">
            ●
          </span>
        )}
      </button>
    );
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        data-testid={testId}
        data-value={value}
        onMouseEnter={openSoon}
        onMouseLeave={scheduleHide}
        onClick={togglePin}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown") {
            e.preventDefault();
            openNow();
          }
        }}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={ariaLabel}
        className={`anim-focus-glow mt-2 w-full flex items-center justify-between gap-2 rounded-md border px-2.5 py-1.5 text-left transition-colors cursor-pointer ${
          open
            ? "border-ds-accent/40 bg-ds-accent/[0.05]"
            : "border-black/[0.12] bg-black/[0.02] hover:bg-black/[0.04]"
        }`}
      >
        <span key={value} className="anim-pop-soft flex items-center gap-1.5 min-w-0 token-13">
          {grouped && current?.group && (
            <span className="shrink-0 token-11 text-glass-muted">{current.group} ·</span>
          )}
          <span className="truncate text-glass">
            {current ? (grouped ? shortTitle(current) : current.title) : value}
          </span>
        </span>
        <span
          className={`anim-chevron shrink-0 text-glass-muted text-[9px] leading-none ${
            open ? "anim-chevron-open" : ""
          }`}
          aria-hidden="true"
        >
          ▶
        </span>
      </button>

      {open &&
        createPortal(
          // Non-glass wrapper carries position + the transform pop; the glass
          // surface inside never transforms (backdrop-filter blur trap).
          <div
            ref={panelRef}
            onMouseEnter={() => {
              clear(hideTimer);
              clear(aimTimer);
            }}
            onMouseLeave={scheduleHide}
            style={{
              position: "fixed",
              top: pos.top,
              left: pos.left,
              width: pos.width || undefined,
              opacity: pos.opacity,
              zIndex: 10000,
              transformOrigin: "top left",
              // "backwards", not the class's "both": a wrapper that keeps a
              // filled animation stays a backdrop root, and the glass surface
              // inside stops blurring what's behind it (text ghosts through).
              animationFillMode: "backwards",
            }}
            className="anim-popover-in"
          >
            <div
              role="menu"
              aria-label={ariaLabel}
              data-testid={`${testId}-panel`}
              className="detail-overlay border border-black/[0.08] rounded-xl shadow-xl p-1 flex"
            >
              {grouped && (
                <div
                  className="w-32 shrink-0 border-r border-black/[0.06] pr-1 flex flex-col gap-0.5"
                  data-testid={`${testId}-rail`}
                >
                  {groups.map((g) => {
                    const shown = g === shownGroup;
                    const holdsCurrent = g === currentGroup;
                    const n = items.filter((i) => i.group === g).length;
                    return (
                      <button
                        key={g}
                        type="button"
                        data-testid={`${testId}-group`}
                        data-group={g}
                        aria-expanded={shown}
                        onMouseEnter={() => possiblySwitch(g)}
                        // Left the grazed row (into the list, or on to another
                        // row) → drop its pending switch. Without this the
                        // re-check fires after the cursor lands in the list,
                        // no longer "aiming", and steals the list anyway.
                        onMouseLeave={() => clear(railTimer)}
                        onFocus={() => switchNow(g)}
                        onClick={() => switchNow(g)}
                        className={`anim-row cursor-pointer flex items-center justify-between gap-2 text-left px-2.5 py-1.5 rounded-lg token-13 transition-colors ${
                          shown
                            ? "bg-black/[0.06] text-glass font-medium"
                            : "text-glass-secondary hover:bg-black/[0.03]"
                        }`}
                      >
                        <span className="flex items-center gap-1.5 min-w-0">
                          <span className="truncate">{g}</span>
                          {holdsCurrent && (
                            <span
                              className="w-1.5 h-1.5 rounded-full bg-ds-accent shrink-0"
                              aria-label="current doc is in this group"
                            />
                          )}
                        </span>
                        <span className="token-11 text-glass-muted shrink-0">{n}</span>
                      </button>
                    );
                  })}
                </div>
              )}
              <div
                ref={listRef}
                className="flex-1 min-w-0 max-h-[60vh] overflow-y-auto pl-1"
                data-testid={`${testId}-list`}
              >
                {/* Re-keyed per group so the cascade replays on every switch. */}
                <div key={shownGroup ?? "all"} className="anim-cascade flex flex-col gap-0.5">
                  {listItems.map(renderItem)}
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
}
